// SignalPath — application shell.
//
// One journey, not two. The prototype's 30-role explorer used to sit in the
// primary nav, which meant a user who had just had their own role analysed was
// dropped into a generic industry list with no connection to what they'd done —
// the flow broke exactly where it should have deepened.
//
// Now: your profile → your exposure → your pathways → your plan. The role
// browser is secondary, reachable but never in the way (ADR-020).

import { useState, useEffect, useRef } from "react";
import Logo from "./components/Logo";
import Footer from "./components/Footer";
import Intake from "./components/Intake";
import Assessment from "./components/Assessment";
import Exposure from "./components/Exposure";
import Phase1 from "./components/Phase1";
import Pathways from "./components/Pathways";
import PathwayWall from "./components/PathwayWall";
import PulseLoader from "./components/PulseLoader";
import Admin from "./components/Admin";
import Account from "./components/Account";
import Learn from "./components/Learn";
import { useAuth, isFirebaseConfigured } from "./lib/auth";
import { projectId } from "./lib/firebase";
import { saveAssessment, loadHistory } from "./lib/profile";
import { recordSignal, EVENTS, exposureBand, confidenceBand } from "./lib/signals";
import { NAVY, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, PROD_PROJECT_ID } from "./utils/constants";

const STEPS = [
  { n: 0, label: "Your Profile", icon: "◎" },
  { n: 1, label: "Your Exposure", icon: "①" },
  { n: 2, label: "Map Pathways", icon: "②" },
  { n: 3, label: "Learn & Apply", icon: "③" },
];

/**
 * Which backend am I looking at?
 *
 * Two Firebase projects now exist — production and `signalpath-demo` — and the
 * only thing selecting between them is a Vite `--mode` flag several layers away
 * from the screen. `npm run dev` and `npm run dev:demo` render identically, and
 * one of them writes real users' assessments.
 *
 * So the shell says it out loud. The check is "is this NOT production", never
 * "is this demo": a mistyped or missing env file then shows the badge rather
 * than hiding it, which is the failure direction we want.
 *
 * Renders nothing on production, and nothing when Firebase is unconfigured —
 * that is the anonymous-only local build (ADR-020) and it writes nowhere.
 */
function EnvBadge() {
  if (!isFirebaseConfigured || projectId === PROD_PROJECT_ID) return null;
  return (
    <div
      role="status"
      title={`This build is writing to the Firebase project "${projectId}", not production.`}
      style={{ position: "fixed", left: 10, bottom: 10, zIndex: 50, pointerEvents: "none",
               background: `${AMBER}18`, border: `1px solid ${AMBER}60`, color: AMBER,
               borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 700,
               letterSpacing: 1, textTransform: "uppercase",
               fontFamily: "'JetBrains Mono', monospace" }}
    >
      {projectId || "no backend"}
    </div>
  );
}

export default function SignalPath() {
  const [step, setStep] = useState(0);
  const [parsed, setParsed] = useState(null);     // raw extraction
  const [profile, setProfile] = useState(null);   // confirmed assessment
  const [tasks, setTasks] = useState(null);
  const [scored, setScored] = useState(null);     // exposure result
  const [target, setTarget] = useState(null);   // chosen destination role
  const [browsing, setBrowsing] = useState(false);
  // The admin console is not in the nav. It is one page for one or two people,
  // and a hash route keeps it out of the product's surface entirely.
  //
  // `#account` is the same idea for the opposite reason: erasure and export are
  // things you go looking for deliberately, and putting them in the journey nav
  // would make every user step past them on the way to their result.
  const readHash = () =>
    typeof window === "undefined" ? null
    : window.location.hash === "#admin" ? "admin"
    : window.location.hash === "#account" ? "account"
    : null;
  const [route, setRoute] = useState(readHash);
  useEffect(() => {
    const onHash = () => setRoute(readHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const { user, ready } = useAuth();
  const savedFor = useRef(null);

  // Prior assessments, loaded ONCE per signed-in user and never refreshed
  // during the session. That is deliberate: this session's own record is
  // written the moment a score exists, and a history that reloaded after the
  // write would include it — comparing the score against itself and reporting
  // "no change" every time (ADR-022).
  const [history, setHistory] = useState(null);
  const loadedFor = useRef(null);
  useEffect(() => {
    if (!user?.uid || loadedFor.current === user.uid) return;
    loadedFor.current = user.uid;
    loadHistory(user.uid, 12).then(setHistory);
  }, [user]);

  // The account wall stands between the exposure score and the pathways
  // (R21, ADR-036). Everything before it is free and ungated, because the
  // exposure screen is the one people share and it costs us nothing to serve.
  //
  // THREE states, not two. The first version computed `walled = configured &&
  // ready && !user` and rendered Pathways whenever that was false — which
  // includes the window before Firebase has restored the session, when `ready`
  // is still false. The gate stood open for exactly as long as auth took to
  // initialise, which on a warm connection is long enough to see the page and
  // click through it. A gate whose default is open is not a gate.
  //
  // So: while we do not yet know who this is, show neither. Unknown is its own
  // state and has to be rendered as one.
  const gateState = !isFirebaseConfigured ? "open"      // no auth configured at all
                  : !ready ? "unknown"                  // still asking Firebase
                  : user ? "open" : "walled";

  // One dated, append-only record per completed diagnosis (R13). Written once
  // the person is signed in and has a score — the ref guards against a re-render
  // writing a duplicate, which would corrupt the very history the record exists
  // to provide.
  // Telemetry for the journey. Fired for everyone, signed in or not — the free
  // journey is anonymous, and stats covering only signed-in users would describe
  // the minority who got past the wall (ADR-038).
  const firedFor = useRef(new Set());
  const fireOnce = (key, event, fields) => {
    if (firedFor.current.has(key)) return;
    firedFor.current.add(key);
    recordSignal(event, fields);
  };

  useEffect(() => {
    if (!scored || !profile) return;
    fireOnce(`scored:${profile.chosenCode}:${scored.exposure}`, EVENTS.EXPOSURE_SCORED, {
      occupationCode: profile.chosenCode,
      exposureBand: exposureBand(scored.exposure),
      confidenceBand: confidenceBand(profile.confidence),
      resolvedVia: profile.resolvedVia,
      escalated: typeof profile.confidence === "number" ? profile.confidence < 0.7 : undefined,
    });
  }, [scored, profile]);

  useEffect(() => {
    if (step === 2 && profile && gateState === "walled") {
      fireOnce(`wall:${profile.chosenCode}`, EVENTS.WALL_SHOWN, { occupationCode: profile.chosenCode });
    }
  }, [step, profile, gateState]);

  useEffect(() => {
    if (!user || !scored || !profile) return;
    const key = `${user.uid}:${profile.chosenCode}:${scored.exposure}`;
    if (savedFor.current === key) return;
    savedFor.current = key;
    saveAssessment(user.uid, {
      occupationCode: profile.chosenCode,
      occupationTitle: profile.chosenTitle,
      confidence: profile.confidence,
      resolvedVia: profile.resolvedVia,
      exposure: scored,
      confirmed: profile.confirmedActivities || [],
    });
  }, [user, scored, profile]);

  // Steps unlock as the journey progresses — and moving back NEVER destroys
  // work. Losing a parsed profile because you clicked a tab is the fastest way
  // to make someone abandon.
  const enabled = (n) =>
    n === 0 || (n === 1 && profile) || (n === 2 && scored) || (n === 3 && target);

  // The legacy Phase2/Phase3 pair is gone. They took a synthesised {job, analysis}
  // shape and asked the model for "4 pathways"; when that call failed — which on
  // the free tier is the normal case — a hand-written fallback table answered,
  // and it was keyed on coarse buckets rather than on the person. That is why
  // every résumé produced the same four destinations. Pathways now derives from
  // O*NET adjacency, this person's own skill vector, and the exposure delta.

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy font-sans">
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div className="absolute -top-[20%] -right-[10%] h-1/2 w-1/2 rounded-full"
             style={{ background: `radial-gradient(circle, ${TEAL}08 0%, transparent 70%)` }} />
        <div className="absolute -bottom-[20%] -left-[10%] h-[60%] w-[60%] rounded-full"
             style={{ background: `radial-gradient(circle, ${TEAL}05 0%, transparent 70%)` }} />
        <div className="absolute inset-0"
             style={{ backgroundImage: `linear-gradient(${TEAL}06 1px, transparent 1px), linear-gradient(90deg, ${TEAL}06 1px, transparent 1px)`,
                      backgroundSize: "60px 60px" }} />
      </div>

      <div className="relative z-10 mx-auto max-w-shell px-4">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3 py-4"
                style={{ borderBottom: `1px solid ${BORDER}` }}>
          <Logo />
          <nav className="flex flex-wrap gap-1" aria-label="Journey">
            {STEPS.map((t) => {
              const active = step === t.n && !browsing;
              const on = enabled(t.n);
              return (
                <button key={t.n} onClick={() => { if (on) { setBrowsing(false); setStep(t.n); } }}
                  disabled={!on} aria-current={active ? "step" : undefined}
                  style={{ background: active ? TEAL : "transparent",
                           color: active ? NAVY : on ? CREAM60 : CREAM30,
                           border: `1px solid ${active ? TEAL : on ? CREAM30 : BORDER}`,
                           borderRadius: 6, padding: "7px 12px", cursor: on ? "pointer" : "default",
                           fontSize: 12, fontWeight: active ? 700 : 500, opacity: on ? 1 : 0.4,
                           display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 11 }}>{t.icon}</span> {t.label}
                </button>
              );
            })}
          </nav>
        </header>

        <main>
          {route === "admin" ? (
            <Admin onBack={() => { window.location.hash = ""; }} />
          ) : route === "account" ? (
            <Account user={user} onBack={() => { window.location.hash = ""; }} />
          ) : browsing ? (
            <>
              <button onClick={() => setBrowsing(false)}
                style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60,
                         padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
                         marginBottom: 18 }}>
                ← Back to your path
              </button>
              <Phase1 onSelectJob={() => setBrowsing(false)} />
            </>
          ) : (
            <>
              {step === 0 && !parsed && <Intake onParsed={setParsed} />}
              {step === 0 && parsed && (
                <Assessment
                  parsed={parsed}
                  onRestart={() => { setParsed(null); setProfile(null); setScored(null); }}
                  onConfirm={async (p) => {
                    setProfile(p);
                    const code = p.chosenCode;
                    const all = (await import("./data/taxonomy/tasks.json")).default;
                    setTasks(code ? all[code] || [] : []);
                    setStep(1);
                  }}
                />
              )}
              {step === 1 && profile && (
                <Exposure
                  parsed={profile}
                  occupationCode={profile.chosenCode}
                  tasks={tasks}
                  history={history}
                  onScored={setScored}
                  onExplore={() => setStep(2)}
                  onBrowse={() => setBrowsing(true)}
                />
              )}
              {step === 2 && profile && gateState === "unknown" && (
                <PulseLoader text="One moment…" />
              )}
              {step === 2 && profile && gateState === "walled" && (
                <PathwayWall
                  scored={scored}
                  occupationTitle={profile.chosenTitle}
                  onDone={() => { /* useAuth flips `user`; the wall unmounts itself */ }}
                />
              )}
              {step === 2 && profile && gateState === "open" && (
                <Pathways
                  profile={profile}
                  occupationCode={profile.chosenCode}
                  currentScore={scored}
                  onChoose={(t) => {
                    setTarget(t);
                    recordSignal(EVENTS.PATHWAY_CHOSEN, {
                      occupationCode: profile.chosenCode, targetCode: t.code,
                    });
                    setStep(3);
                  }}
                  onBack={() => setStep(1)}
                />
              )}
              {step === 3 && (
                <Learn
                  target={target}
                  profile={profile}
                  currentCode={profile?.chosenCode}
                  onBack={() => setStep(2)}
                />
              )}
            </>
          )}
        </main>

        <Footer user={user} />
      </div>

      <EnvBadge />
    </div>
  );
}
